import { useState, useEffect } from 'react';

/**
 * Reads the 7 CSS variables that chart widgets actually use.
 * Falls back to the original hardcoded hex values so charts still render
 * correctly in environments where CSS vars are not yet resolved.
 */
function readColors() {
  const s = getComputedStyle(document.documentElement);
  const get = (v) => s.getPropertyValue(v).trim();
  return {
    foreground: get('--foreground') || '#313131',
    muted:      get('--muted')      || '#f9f9f9',
    background: get('--background') || '#ffffff',
    chart1:     get('--chart-1')    || '#313131',
    chart2:     get('--chart-2')    || '#f19f57',
    chart3:     get('--chart-3')    || '#ff8b6c',
    chart5:     get('--chart-5')    || '#44b672',
  };
}

/**
 * Returns the current chart color palette derived from CSS variables.
 *
 * Re-reads on every theme change — both dark-mode class toggles on <html>
 * and Tweakcn <style> injections into <head> are detected via MutationObserver.
 *
 * Usage:
 *   const colors = useChartColors();
 *   // colors.chart1, colors.chart2, colors.foreground, etc.
 */
export function useChartColors() {
  const [colors, setColors] = useState(readColors);

  useEffect(() => {
    const refresh = () => setColors(readColors());

    const observer = new MutationObserver(refresh);

    // Detect dark-mode class toggle (e.g. className="dark") on <html>
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class', 'style'],
    });

    // Detect Tweakcn injecting / replacing <style> tags in <head>
    observer.observe(document.head, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, []);

  return colors;
}
